import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Input, Button, Select, Tag, Space, Typography, Image, Divider } from "antd";
import { CheckCircleTwoTone } from "@ant-design/icons";
import ShopSelect from "./common/ShopSelect";
import {
  registerDevice,
  getDeviceByDeviceId,
  bindDeviceConfigShop,
  updateVisit,
  listConfigs,
  listPictures,
} from "../network/api";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";

const { Text } = Typography;
const Label = ({ children }) => (
  <Text type="secondary" style={{ display: "block", marginBottom: 4, fontSize: 13 }}>{children}</Text>
);

// D2-T5 — folds the Edge360 onboarding (register → outlet → config → activate →
// first-image validation → retailer ack) into an install visit.
export default function InstallActivation({ visit, canEdit, onChange }) {
  const [deviceId, setDeviceId] = useState(visit.deviceId || "");
  const [device, setDevice] = useState(null); // camera w/ populated config + shop
  const [registering, setRegistering] = useState(false);
  const [shopId, setShopId] = useState(visit.shop?._id);
  const [configId, setConfigId] = useState();
  const [configs, setConfigs] = useState([]);
  const [activating, setActivating] = useState(false);
  const [picture, setPicture] = useState(null);
  const [checkingImg, setCheckingImg] = useState(false);
  const [ack, setAck] = useState({ name: visit.retailerAck?.name || "", contact: visit.retailerAck?.contact || "" });
  const [savingAck, setSavingAck] = useState(false);

  useEffect(() => {
    listConfigs({ page: 1, pageSize: 200 })
      .then((r) => setConfigs((r.data || []).map((c) => ({ value: c._id, label: c.title }))))
      .catch(() => setConfigs([]));
  }, []);

  const loadDevice = useCallback(async (id) => {
    if (!id) return;
    try {
      const d = await getDeviceByDeviceId(id);
      setDevice(d);
      if (d.config?._id) setConfigId(d.config._id);
      if (d.shop?._id) setShopId(d.shop._id);
    } catch (e) {
      /* not registered yet */
    }
  }, []);

  useEffect(() => {
    if (visit.deviceId) loadDevice(visit.deviceId);
  }, [visit.deviceId, loadDevice]);

  const onRegister = async () => {
    const id = deviceId.trim();
    if (!id) return notify.warning("Enter the device ID shown on the Edge360");
    setRegistering(true);
    try {
      const cam = await registerDevice({ deviceId: id, deviceTitle: visit.shop?.title || undefined, shop: visit.shop?._id || undefined });
      await updateVisit(visit._id, { camera: cam._id, deviceId: cam.deviceId });
      setDevice(cam);
      notify.success("Device registered & linked to this visit");
      onChange?.();
    } catch (e) {
      notify.error(serverError(e, "Failed to register device"));
    } finally {
      setRegistering(false);
    }
  };

  const onActivate = async () => {
    if (!deviceId) return notify.warning("Register the device first");
    if (!configId && !shopId) return notify.warning("Pick a config and/or outlet");
    setActivating(true);
    try {
      await bindDeviceConfigShop(deviceId, { config: configId || undefined, shop: shopId || undefined });
      await loadDevice(deviceId);
      notify.success("Config & outlet applied to the device");
      onChange?.();
    } catch (e) {
      notify.error(serverError(e, "Failed to activate device"));
    } finally {
      setActivating(false);
    }
  };

  const onCheckImage = async () => {
    if (!deviceId) return;
    setCheckingImg(true);
    try {
      const r = await listPictures(deviceId, { page: 1, pageSize: 1 });
      const p = (r.data || [])[0];
      setPicture(p || null);
      (p ? notify.success : notify.info)(p ? "First image received ✓" : "No image yet — trigger a capture on the device");
    } catch (e) {
      notify.error(serverError(e, "Failed to check images"));
    } finally {
      setCheckingImg(false);
    }
  };

  const onSaveAck = async () => {
    if (!ack.name.trim()) return notify.warning("Enter the retailer name");
    setSavingAck(true);
    try {
      await updateVisit(visit._id, { retailerAck: { name: ack.name.trim(), contact: ack.contact.trim(), acknowledgedAt: new Date() } });
      notify.success("Retailer acknowledgement recorded");
      onChange?.();
    } catch (e) {
      notify.error(serverError(e, "Failed to save acknowledgement"));
    } finally {
      setSavingAck(false);
    }
  };

  const registered = !!(device || visit.deviceId);
  const configBound = !!device?.config;
  const outletSet = !!device?.shop;
  const imageOk = !!picture;
  const ackDone = !!visit.retailerAck?.acknowledgedAt;

  const StatusTag = ({ ok, children }) => (
    <Tag color={ok ? "success" : "default"} icon={ok ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : null}>
      {children}
    </Tag>
  );

  return (
    <Card title="Device Activation">
      <Space wrap style={{ marginBottom: 18 }}>
        <StatusTag ok={registered}>Registered</StatusTag>
        <StatusTag ok={configBound}>Config bound</StatusTag>
        <StatusTag ok={outletSet}>Outlet set</StatusTag>
        <StatusTag ok={imageOk}>First image</StatusTag>
        <StatusTag ok={ackDone}>Retailer ack</StatusTag>
      </Space>

      {/* 1 — Register */}
      <Text strong>1 · Register device</Text>
      <Row gutter={16} align="bottom" style={{ marginTop: 8 }}>
        <Col flex="auto">
          <Label>Device ID (shown on the Edge360 hotspot page)</Label>
          <Input value={deviceId} onChange={(e) => setDeviceId(e.target.value)} placeholder="e.g. 12345678" disabled={!canEdit || registered} />
        </Col>
        <Col>
          <Button type="primary" loading={registering} disabled={!canEdit || registered} onClick={onRegister}>
            {registered ? "Registered" : "Register device"}
          </Button>
        </Col>
      </Row>

      <Divider style={{ margin: "16px 0" }} />

      {/* 2 — Outlet + config */}
      <Text strong>2 · Bind outlet &amp; configuration</Text>
      <Row gutter={16} style={{ marginTop: 8 }}>
        <Col xs={24} sm={10}>
          <Label>Outlet</Label>
          <ShopSelect value={shopId} onChange={setShopId} current={visit.shop || device?.shop} allowClear />
        </Col>
        <Col xs={24} sm={10}>
          <Label>Configuration</Label>
          <Select style={{ width: "100%" }} value={configId} onChange={setConfigId} options={configs} showSearch optionFilterProp="label" placeholder="Select config" allowClear />
        </Col>
        <Col xs={24} sm={4} style={{ display: "flex", alignItems: "flex-end" }}>
          <Button loading={activating} disabled={!canEdit || !registered} onClick={onActivate} block>Activate</Button>
        </Col>
      </Row>

      <Divider style={{ margin: "16px 0" }} />

      {/* 3 — First image */}
      <Text strong>3 · Validate first image</Text>
      <div style={{ marginTop: 8 }}>
        <Space wrap>
          <Button loading={checkingImg} disabled={!registered} onClick={onCheckImage}>Check for first image</Button>
          {picture && (
            <Space>
              <Image src={picture.absolutePath} width={56} height={42} style={{ objectFit: "cover", borderRadius: 4 }} />
              <Text type="secondary">captured {relativeTime(picture.createdAt)}</Text>
            </Space>
          )}
        </Space>
      </div>

      <Divider style={{ margin: "16px 0" }} />

      {/* 4 — Retailer acknowledgement */}
      <Text strong>4 · Retailer acknowledgement</Text>
      <Row gutter={16} align="bottom" style={{ marginTop: 8 }}>
        <Col xs={24} sm={9}>
          <Label>Retailer name</Label>
          <Input value={ack.name} onChange={(e) => setAck((a) => ({ ...a, name: e.target.value }))} disabled={!canEdit} />
        </Col>
        <Col xs={24} sm={9}>
          <Label>Contact</Label>
          <Input value={ack.contact} onChange={(e) => setAck((a) => ({ ...a, contact: e.target.value }))} disabled={!canEdit} />
        </Col>
        <Col xs={24} sm={6} style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          <Button loading={savingAck} disabled={!canEdit} onClick={onSaveAck}>Record</Button>
          {ackDone && <Text type="secondary" style={{ fontSize: 12 }}>✓ {relativeTime(visit.retailerAck.acknowledgedAt)}</Text>}
        </Col>
      </Row>
    </Card>
  );
}
